/**
 * Live readings for the telemetry strip (Sprint 2, S2-04): speed, distance to the Sun, one-way light time to Earth,
 * mission day and the distance to the body whose bubble the spacecraft is in. Allocation-free (output parameter).
 */
import { AU_KM } from './units';
import type { Vec3 } from './vec3';

/** Speed of light, km/s. */
export const C_KMS = 299_792.458;
const DAY_MS = 86_400_000;
const DAY_S = 86_400;

export interface Telemetry {
  /** Heliocentric speed, km/s. */
  speedKms: number;
  /** Distance to the Sun, AU. */
  sunDistanceAu: number;
  /** One-way light time to Earth, seconds. */
  lightTimeS: number;
  /** Mission day: 1 on the launch day (counted in 24 h steps from the launch time). */
  missionDay: number;
  /** Distance to the bubble body's centre, km (NaN when outside every bubble). */
  nearDistanceKm: number;
  /** Speed relative to the bubble body, km/s (NaN when outside every bubble). */
  nearSpeedKms: number;
}

export function createTelemetry(): Telemetry {
  return {
    speedKms: 0,
    sunDistanceAu: 0,
    lightTimeS: 0,
    missionDay: 0,
    nearDistanceKm: Number.NaN,
    nearSpeedKms: Number.NaN,
  };
}

const dist = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

/**
 * @param p heliocentric position, km
 * @param v heliocentric velocity, km/day (as in `SpacecraftState`)
 * @param earth Earth's heliocentric position, km
 * @param near position of the body whose bubble the spacecraft is in, or null
 * @param nearV the spacecraft's velocity relative to that body, km/day, or null
 */
export function computeTelemetry(
  out: Telemetry,
  p: Vec3,
  v: Vec3,
  earth: Vec3,
  near: Vec3 | null,
  nearV: Vec3 | null,
  launchUtcMs: number,
  nowUtcMs: number,
): Telemetry {
  out.speedKms = Math.hypot(v.x, v.y, v.z) / DAY_S;
  out.sunDistanceAu = Math.hypot(p.x, p.y, p.z) / AU_KM;
  out.lightTimeS = dist(p, earth) / C_KMS;
  out.missionDay = Math.floor((nowUtcMs - launchUtcMs) / DAY_MS) + 1;
  out.nearDistanceKm = near ? dist(p, near) : Number.NaN;
  out.nearSpeedKms = near && nearV ? Math.hypot(nearV.x, nearV.y, nearV.z) / DAY_S : Number.NaN;
  return out;
}

/** Missions shorter than this show the mission elapsed time instead of the mission day (Sprint 4, D13). */
export const MET_MAX_DAYS = 30;

/** Whether a mission from `launchMs` to `endMs` (UTC ms; null while active) is told in mission elapsed time. */
export function usesMet(launchMs: number, endMs: number | null): boolean {
  return endMs !== null && endMs - launchMs < MET_MAX_DAYS * DAY_MS;
}

/**
 * Mission elapsed time as Apollo counted it, `hhh:mm:ss` with at least three hour digits (`102:45:40`), whole
 * seconds (truncated); null before launch (nothing to count yet).
 */
export function formatMet(elapsedMs: number): string | null {
  if (!(elapsedMs >= 0)) return null;
  const s = Math.floor(elapsedMs / 1000);
  const pad = (n: number, w = 2): string => String(n).padStart(w, '0');
  return `${pad(Math.floor(s / 3600), 3)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}
